<template>
    <!-- Reads as one more line of the conversation, in the chat's own text style. -->
    <p
        v-if="!suppressed && phase === 'dismissed'"
        class="mt-4 px-2 text-ink"
        :class="isNadiaChatRefreshEnabled ? 'body-small' : 'nadia-chat'"
        data-testid="chat-performance-picker-dismissed">
        {{ t("chat.performancePicker.dismissed") }}
    </p>
    <!-- Already picked a task on an earlier visit: stay out of the way, but keep a way back in. -->
    <BaseCard
        v-else-if="!suppressed && collapsed"
        theme="cream"
        radius="lgr"
        padding="none"
        elevated
        class="picker-card w-full mt-4 flex items-center gap-3 px-5 py-4"
        data-testid="chat-performance-picker-collapsed">
        <h2 class="grow min-w-0 nadia-chat-bold text-ink truncate">{{ t("chat.performancePicker.collapsedTitle") }}</h2>
        <BaseButton theme="neutral" size="sm" class="shrink-0" data-testid="picker-reopen" @click="expand">{{ t("chat.performancePicker.show") }}</BaseButton>
    </BaseCard>
    <!-- While the picker data loads, the chat's own thinking loader stands in for the card. -->
    <NadiaThinkingShapes v-else-if="!suppressed && isFetching && isNadiaChatRefreshEnabled" :status-text="t('chat.performancePicker.loading')" class="my-4 px-2" />
    <ChatThinkingMessage v-else-if="!suppressed && isFetching" :messages="[t('chat.performancePicker.loading')]" class="my-4" />
    <BaseCard
        v-else-if="!suppressed && hasMoments"
        theme="cream"
        radius="lgr"
        padding="none"
        elevated
        class="picker-card w-full mt-4 flex flex-col px-5"
        data-testid="chat-performance-picker">
        <!-- Equal side columns keep the title centred whether or not there is a way back. The
             negative margin runs the divider to the card's edges. -->
        <div class="-mx-5 grid grid-cols-[minmax(2rem,1fr)_minmax(0,auto)_minmax(2rem,1fr)] items-center gap-3 border-b border-border px-5 pt-5 pb-4">
            <BaseButton
                v-if="canGoBack"
                theme="ghost"
                size="sm"
                icon-only
                type="button"
                :aria-label="backLabel"
                class="justify-self-start"
                data-testid="picker-back"
                @click="goBack">
                <i class="bi bi-chevron-left" aria-hidden="true"></i>
            </BaseButton>
            <span v-else></span>
            <h2 class="nadia-chat-bold text-ink text-center min-w-0 truncate">{{ phaseTitle }}</h2>
            <BaseButton
                theme="ghost"
                size="sm"
                icon-only
                type="button"
                :aria-label="t('common.close')"
                class="justify-self-end"
                :disabled="isDismissing"
                data-testid="picker-close"
                @click="dismiss('close_button')">
                <i class="bi bi-x-lg" aria-hidden="true"></i>
            </BaseButton>
        </div>
        <PmPickerMomentPhase v-if="phase === 'moment'" :moments="moments" @select="selectMoment" @dismiss="dismiss('something_else')" />
        <PmPickerTaskPhase
            v-else-if="phase === 'task' && moment"
            :self-reviews="selfReviews"
            :team-reviews="teamReviews"
            :team-label="teamLabel"
            :show-empty-team-row="canAddReviewees"
            :current-conversation-hashid="currentConversationHashid"
            :is-busy="isLaunching"
            :start-disabled="isExpired"
            @row-clicked="handleRowClick"
            @open-team="openTeamPhase" />
        <PmPickerPersonPhase
            v-else-if="phase === 'person' && moment"
            :team-reviews="teamReviews"
            :can-add-relationships="pickerData?.can_add_relationships ?? false"
            :current-conversation-hashid="currentConversationHashid"
            :is-busy="isLaunching"
            :start-disabled="isExpired"
            :preselected-relationship-id="preselection?.relationship_id"
            :initial-search="preselection?.person_to_add"
            @row-clicked="handleRowClick"
            @open-person="openPerson"
            @person-added="refresh" />
        <!-- One reviewee's tasks get their own step, as a moment's tasks do, titled with their name. -->
        <PmPickerTaskPhase
            v-else-if="phase === 'person-tasks' && personReviews.length"
            :self-reviews="personReviews"
            :team-reviews="[]"
            team-label=""
            :current-conversation-hashid="currentConversationHashid"
            :is-busy="isLaunching"
            :start-disabled="isExpired"
            @row-clicked="handleRowClick" />
        <!-- The negative margin runs the divider to the card's edges; ms-2.5 on the link lines it up with the row labels. -->
        <div class="-mx-5 flex border-t border-border px-5 py-4 scaffold-body-small">
            <BaseLink
                :href="performancePageUrl"
                class="ms-2.5 flex min-w-0 items-center gap-2 text-ink-muted hover:text-ink transition-colors"
                data-testid="picker-page-link"
                :before="logPageLinkClick">
                <span class="min-w-0 truncate">{{ performancePageLabel }}</span>
                <i class="bi bi-box-arrow-up-right shrink-0 text-sm/none" aria-hidden="true"></i>
            </BaseLink>
        </div>
        <BaseToast v-model:open="isErrorToastOpen" theme="error" :description="launchError" />
    </BaseCard>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { DismissPerformancePickerRequest, DismissPerformancePickerResponse, PickerDismissalMethod } from "~vue/api/generated/chat";
import type { OrganizationDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import { useNadiaChatRefreshEnabled } from "~vue/composables/useNadiaChatRefresh";
import { usePerformancePicker } from "~vue/Moments/composables/usePerformancePicker";
import { useStartPerformanceReviewConversation } from "~vue/Moments/composables/useStartPerformanceReviewConversation";
import BaseLink from "~vue/router/BaseLink.vue";
import type { DecoratedPerformanceReview, PmPickerPreselection } from "~vue/types/performance_management";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, onMounted, onUnmounted, type Ref, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import { ACTION } from "../chatActions";
import ChatThinkingMessage from "../ChatThinkingMessage.vue";
import NadiaThinkingShapes from "../NadiaThinkingShapes.vue";
import PmPickerMomentPhase from "./pmPicker/PmPickerMomentPhase.vue";
import PmPickerPersonPhase from "./pmPicker/PmPickerPersonPhase.vue";
import PmPickerTaskPhase from "./pmPicker/PmPickerTaskPhase.vue";
import { performancePickerGateKey } from "./usePerformancePickerGate";

interface PickerAction {
    index?: number;
    action_params?: Record<string, unknown>;
    action_state?: { minimized?: boolean; dismissed?: boolean } | null;
}

interface Props {
    // UIDirectiveRenderer contract: the action line plus message coordinates. Absent when
    // rendered as a mainline tool card via ChatMessageToolCall, which v-binds the toolCalls
    // map entry instead.
    data?: PickerAction & { action_name: string };
    messageId?: number | string;
    lineIdx?: number;
    // Tool-card path: the persisted tool-result action line from the toolCalls map entry.
    // Its action_params carry the server-resolved `preselection` hints, and its action_state
    // carries `minimized` once the user has picked a task in an earlier visit.
    action?: PickerAction | null;
}

const props = defineProps<Props>();

// The tool-card path spreads its whole map entry ({name, completed, action, ...}); keep
// those extras off the rendered DOM.
defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const isNadiaChatRefreshEnabled = useNadiaChatRefreshEnabled();

// Hashid of the coaching session this picker is rendered inside ("" in discovery chats).
const currentConversationHashid = inject<string>("coachingSessionId", "");
const sourceSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);

type ParsedPreselection = Omit<PmPickerPreselection, "matched">;

// Defensive parse: the preselection rides a persisted tool result, so treat it as untyped input.
// It only seeds the *initial* phase/selection — statuses always come from the live fetch.
function parsePreselection(raw: unknown): ParsedPreselection | null {
    if (!raw || typeof raw !== "object") {
        return null;
    }
    const record = raw as Record<string, unknown>;
    const parsed: ParsedPreselection = {
        moment_id: typeof record.moment_id === "number" ? record.moment_id : null,
        cta_id: typeof record.cta_id === "number" ? record.cta_id : null,
        team: record.team === true,
        relationship_id: typeof record.relationship_id === "string" ? record.relationship_id : null,
        person_to_add: typeof record.person_to_add === "string" ? record.person_to_add : null,
    };
    const hasAnyHint = parsed.moment_id !== null || parsed.cta_id !== null || parsed.team || parsed.relationship_id !== null || parsed.person_to_add !== null;
    return hasAnyHint ? parsed : null;
}

const pickerAction = computed(() => props.action ?? props.data);
const preselection = parsePreselection(pickerAction.value?.action_params?.preselection);

// The tool found nothing the user can work in and no request for the picker itself, so this
// result has no card. Results persisted before the flag existed still render.
const suppressed = computed(() => pickerAction.value?.action_params?.show_card === false);

type Phase = "moment" | "task" | "person" | "person-tasks" | "dismissed";
const phase = ref<Phase>(pickerAction.value?.action_state?.dismissed ? "dismissed" : "moment");
const selectedMomentId = ref<number | null>(preselection?.moment_id ?? null);

// Collapsed on arrival when a previous visit ended in a pick. Rendering the summary costs
// no fetch; the payload is only loaded if the user expands.
const collapsed = ref(!!pickerAction.value?.action_state?.minimized);

const { data: pickerData, isFetching, execute, decoratedReviews } = usePerformancePicker(selectedMomentId);

const moments = computed(() => pickerData.value?.moments ?? []);
const hasMoments = computed(() => moments.value.length > 0);
const pickerGate = inject(performancePickerGateKey, null);
const flowImprovementsEnabled = inject<Ref<boolean>>("performanceManagementFlowImprovements", ref(false));
const pickerGateId = `${props.messageId}:${pickerAction.value?.index}`;
const loading = ref(true);
watchEffect(() => pickerGate?.setOpen(pickerGateId, !suppressed.value && !collapsed.value && (loading.value || hasMoments.value) && phase.value !== "dismissed"));
watch(
    () => pickerAction.value?.action_state,
    (state) => {
        if (state?.dismissed) {
            phase.value = "dismissed";
        } else if (state?.minimized) {
            collapsed.value = true;
        }
    },
    { deep: true },
);
onUnmounted(() => pickerGate?.setOpen(pickerGateId, false));
const moment = computed(() => pickerData.value?.moment ?? null);
const isExpired = computed(() => moment.value?.state === "expired");
const selectableMoments = computed(() => moments.value.filter((summary) => summary.state !== "locked"));

const selfReviews = computed(() => decoratedReviews.value.filter((review) => review.cta?.reviewer_target === "self"));
const teamReviews = computed(() => decoratedReviews.value.filter((review) => review.cta?.reviewer_target === "connection"));

// The reviewee whose tasks the person-tasks step shows.
const selectedRelationshipId = ref<string | null>(null);
const reviewsFor = (relationshipId: string) => teamReviews.value.filter((review) => review.performance_review_relationship_id === relationshipId);
const personReviews = computed(() => (selectedRelationshipId.value ? reviewsFor(selectedRelationshipId.value) : []));
// Reviewees are added from the person phase, so that phase is worth opening on an empty team
// list — but only where the cycle asks for team reviews and the add controls are available.
const canAddReviewees = computed(() => !!pickerData.value?.can_add_relationships && (moment.value?.ctas ?? []).some((cta) => cta.reviewer_target === "connection"));

// The team row's name mirrors the PM page and the cycle tracker: an org-configured label wins.
const teamLabel = computed(() => moment.value?.other_moment?.title || t("chat.performancePicker.teamLabel"));

const phaseTitles = computed<Record<Exclude<Phase, "dismissed">, string>>(() => ({
    moment: t("chat.performancePicker.phaseTitle.moment"),
    task: t("chat.performancePicker.phaseTitle.task"),
    person: t("chat.performancePicker.phaseTitle.person"),
    "person-tasks": t("chat.performancePicker.phaseTitle.person"),
}));
const phaseTitle = computed(() => {
    if (phase.value === "dismissed") {
        return "";
    }
    // The task phase is a specific moment's task list — name the moment so the user knows
    // where they are (the back link below carries the return path instead).
    if (phase.value === "task") {
        return moment.value?.title || phaseTitles.value.task;
    }
    if (phase.value === "person-tasks") {
        return personReviews.value[0]?.relationship?.reviewee_name || phaseTitles.value["person-tasks"];
    }
    return phaseTitles.value[phase.value];
});

const backLabel = computed(() => {
    if (phase.value === "person-tasks") {
        return t("chat.performancePicker.backTo", { title: teamLabel.value });
    }
    if (phase.value === "person") {
        return moment.value?.title ? t("chat.performancePicker.backTo", { title: moment.value.title }) : t("chat.performancePicker.back");
    }
    return t("chat.performancePicker.backToAllMoments");
});

// A team hint opens the reviewee list, or straight onto one reviewee's tasks when they have several.
function teamPhase(relationshipId: string | null): Phase {
    if (relationshipId && reviewsFor(relationshipId).length > 1) {
        selectedRelationshipId.value = relationshipId;
        return "person-tasks";
    }
    return teamReviews.value.length || canAddReviewees.value ? "person" : "task";
}

function initialPhase(): Phase {
    if (!moment.value) {
        return "moment";
    }
    if (preselection?.relationship_id || preselection?.team) {
        return teamPhase(preselection.relationship_id);
    }
    if ((preselection?.cta_id ?? null) !== null || (preselection?.moment_id ?? null) !== null) {
        return "task";
    }
    // No hints: nothing to choose when there's a single selectable moment.
    return selectableMoments.value.length <= 1 ? "task" : "moment";
}

// One impression event for every way the picker can appear: each wizard phase, and the
// collapsed summary on return visits. `is_reopen` marks the impression that followed a
// click on the collapsed summary, so first-open and re-open funnels stay separable.
function logImpression(loggedPhase: Phase | "collapsed", isReopen: boolean) {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_VIEWED, {
        moment_id: loggedPhase === "collapsed" ? selectedMomentId.value : moment.value?.id,
        phase: loggedPhase,
        preselected: !!preselection,
        is_reopen: isReopen,
    });
}

async function load(isReopen = false) {
    try {
        await execute();
    } finally {
        loading.value = false;
    }
    if (phase.value === "dismissed" || suppressed.value || !hasMoments.value) {
        return;
    }
    if (selectedMomentId.value === null) {
        selectedMomentId.value = pickerData.value?.default_moment_id ?? null;
    }
    phase.value = initialPhase();
    logImpression(phase.value, isReopen);
}

onMounted(async () => {
    if (suppressed.value || phase.value === "dismissed") {
        return;
    }
    // Arriving collapsed is itself an impression — and the only one this visit unless the
    // user expands, so it can't be inferred from the phase events.
    if (collapsed.value) {
        logImpression("collapsed", false);
        return;
    }
    await load();
});

// Re-opening is for this visit only: the persisted `minimized` stays set, so the next
// visit still opens collapsed rather than undoing the choice they already made.
async function expand() {
    collapsed.value = false;
    await load(true);
}

async function selectMoment(momentId: number) {
    selectedMomentId.value = momentId;
    phase.value = "task";
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_MOMENT_SELECTED, { moment_id: momentId });
    await execute();
}

// Phase moves that aren't a moment choice (which logs itself and implies the move forward).
function logPhaseChange(fromPhase: Phase, toPhase: Phase, direction: "forward" | "back") {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_PHASE_SELECTED, {
        moment_id: moment.value?.id,
        from_phase: fromPhase,
        to_phase: toPhase,
        direction,
    });
}

function openTeamPhase() {
    logPhaseChange(phase.value, "person", "forward");
    phase.value = "person";
}

function openPerson(relationshipId: string) {
    selectedRelationshipId.value = relationshipId;
    logPhaseChange(phase.value, "person-tasks", "forward");
    phase.value = "person-tasks";
}

const canGoBack = computed(() => {
    if (phase.value === "person" || phase.value === "person-tasks") {
        return true;
    }
    return phase.value === "task" && selectableMoments.value.length > 1;
});

const BACK_TARGET: Partial<Record<Phase, Phase>> = { "person-tasks": "person", person: "task" };

function goBack() {
    const target = BACK_TARGET[phase.value] ?? "moment";
    logPhaseChange(phase.value, target, "back");
    phase.value = target;
}

const dismissalPayload = ref<DismissPerformancePickerRequest | null>(null);
const {
    execute: saveDismissal,
    error: dismissalError,
    isFetching: isDismissing,
} = useFetch(() => `/v1/chat/performance-management/picker/${props.messageId}/${pickerAction.value?.index}/dismiss`, { immediate: false })
    .post(dismissalPayload)
    .json<DismissPerformancePickerResponse>();
const updateActionState = inject<((payload: { message_id: number | string; lineIdx: number; action_state: { dismissed: boolean } }) => void) | null>("updateActionState", null);

async function dismiss(method: PickerDismissalMethod) {
    if (isDismissing.value) {
        return;
    }
    if (flowImprovementsEnabled.value && props.messageId !== undefined && props.lineIdx !== undefined && pickerAction.value?.index !== undefined) {
        if (phase.value === "dismissed") {
            return;
        }
        dismissalPayload.value = { dismissal_method: method, phase: phase.value, moment_id: moment.value?.id ?? null };
        await saveDismissal();
        if (dismissalError.value) {
            launchError.value = t("chat.performancePicker.dismissFailed");
            isErrorToastOpen.value = true;
            return;
        }
        updateActionState?.({ message_id: props.messageId, lineIdx: props.lineIdx, action_state: { dismissed: true } });
    } else {
        logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_CLOSED, {
            moment_id: moment.value?.id,
            phase: phase.value,
        });
        reportClick({ type: "close", phase: phase.value, moment_id: moment.value?.id ?? null });
    }
    phase.value = "dismissed";
}

// Escape hatch: the full PM page, opened at the currently selected cycle when there is one.
const performancePageUrl = computed(() => (selectedMomentId.value === null ? "/performance_management" : `/performance_management?moment_id=${selectedMomentId.value}`));

// Orgs can rename the PM page (perf_review_custom_copy.page_tab_label) — mirror the nav's label.
const organizationDetails = inject<Ref<OrganizationDetails | null> | undefined>("organization_details", undefined);
const performancePageLabel = computed(() => organizationDetails?.value?.perf_review_custom_copy?.page_tab_label || t("chat.performancePicker.defaultPageLabel"));

function logPageLinkClick() {
    const logged = logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_PAGE_LINK_CLICKED, {
        moment_id: selectedMomentId.value,
        phase: phase.value,
    });
    reportClick({ type: "page_link", phase: phase.value, moment_id: selectedMomentId.value });
    return logged;
}

async function refresh() {
    await execute();
}

const { isLaunching, launchError, startConversation } = useStartPerformanceReviewConversation();

const isErrorToastOpen = ref(false);

// Clicks are persisted on the tool-result row so the session debug view can show what the
// user did with the card. Only the tool-card path has message coordinates to persist
// against, so without them this is a no-op. A row click navigates away and also collapses
// the card, so its event carries the minimized state for the local line update.
const globalProperties = inject<{ emitter?: { emit: (event: string, payload: unknown) => void } } | undefined>("globalProperties", undefined);

interface PickerClick {
    type: "row" | "close" | "page_link";
    phase: Phase;
    moment_id: number | null;
    cta_id?: number;
    task_label?: string;
    reviewer_target?: string;
    relationship_id?: string;
    person_name?: string;
    resumed?: boolean;
    session_hashid?: string;
}

function reportClick(click: PickerClick) {
    if (props.messageId === undefined || props.lineIdx === undefined) {
        return;
    }
    globalProperties?.emitter?.emit(ACTION.PM_PICKER, {
        message_id: props.messageId,
        lineIdx: props.lineIdx,
        subaction: "record_click",
        click,
        action_params: props.action?.action_params,
        ...(click.type === "row" ? { action_state: { minimized: true } } : {}),
    });
}

// The chat URL's trailing segment is the session hashid (chat:chat routes on it).
function sessionHashidFromUrl(url: string): string | undefined {
    return url.split("?")[0].split("/").filter(Boolean).pop();
}

function rowClick(review: DecoratedPerformanceReview, resumed: boolean, url: string): PickerClick {
    return {
        type: "row",
        phase: phase.value,
        moment_id: moment.value?.id ?? null,
        cta_id: review.performance_moment_cta_id,
        task_label: review.cta?.label,
        reviewer_target: review.cta?.reviewer_target,
        relationship_id: review.relationship?.id ? String(review.relationship.id) : undefined,
        person_name: review.relationship?.reviewee_name,
        resumed,
        session_hashid: sessionHashidFromUrl(url),
    };
}

const handleRowClick = async (review: DecoratedPerformanceReview) => {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_ROW_CLICKED, {
        moment_id: moment.value?.id,
        review_id: review.id,
        cta_id: review.performance_moment_cta_id,
        has_conversation: !!review.conversation,
        // The phase the click came from, and (for team reviews) who it was about — without
        // these, a self review and a team review are indistinguishable at the event level.
        phase: phase.value,
        reviewer_target: review.cta?.reviewer_target ?? null,
        relationship_id: review.relationship?.id ? String(review.relationship.id) : null,
    });

    // Resume/View: the conversation already exists — plain navigation, never reseed.
    if (review.conversation?.coaching_session_url) {
        reportClick(rowClick(review, true, review.conversation.coaching_session_url));
        openUrl(review.conversation.coaching_session_url);
        return;
    }

    // Expired cycles allow viewing existing conversations but never new starts (the backend
    // rejects them anyway).
    if (isExpired.value) {
        return;
    }

    const url = await startConversation(review, sourceSessionUuid);
    if (!url) {
        // A session-ended launch resolves null with an empty launchError: the modal dialog owns
        // that messaging, and a blank toast behind it would only add noise.
        if (launchError.value) {
            isErrorToastOpen.value = true;
        }
        return;
    }
    reportClick(rowClick(review, false, url));
    openUrl(url);
};
</script>

<style scoped>
.picker-card {
    animation: picker-in 0.28s ease-out both;
}

@keyframes picker-in {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
}

@media (prefers-reduced-motion: reduce) {
    .picker-card {
        animation: none;
    }
}
</style>
